import {
  CreateTourFromScratch,
  CreateTourModal,
} from '@carboncopyco/components';
import {
  useCreateTourFromUrlMutation,
  useGetDemosQuery,
} from '@carboncopyco/data';
import { isDesktop } from '@carboncopyco/mimesis-shared';
import type { ScreenResolution, Tour } from '@carboncopyco/types';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTourSelector } from '../../../../../hooks';
import { useTourEditorPageProps } from '../../../../../pages/TourEditor/TourEditorPageContext';
import { selectCapturedScreenSize } from '../../../../../store';
import type { UseNavigate } from '../../../../../types/dependencies';
import { getScreenResolutionTypeFromWidth } from '../../../../../utils/getScreenResolutionTypeFromWidth';
import Dropdown from './Dropdown';

interface IAddingFrames {
  originalTourId: string;
  selectedFrameIndex: number;
  demoIdsTourIsBasedOn?: number[];
  deeplinkProtocol: string;
  tour: Tour;
  onDropdownButtonClick: () => void;
  useNavigate: UseNavigate;
  openRedirectPage: (url: string) => void;
}

const title = 'Add new frames to your tour';

const getScreenResolutionTypeFromSettings = (
  tour: Tour,
  capturedScreenSize: {
    width: number;
    height: number;
  }
) => {
  const type = tour?.draftSettings?.screenResolution?.type;
  const responsiveness = tour?.draftSettings?.responsiveness;

  if (!type) {
    return responsiveness
      ? 'responsive'
      : getScreenResolutionTypeFromWidth(capturedScreenSize?.width);
  }

  if (type === 'responsive') {
    return responsiveness
      ? 'responsive'
      : getScreenResolutionTypeFromWidth(
          tour?.draftSettings?.screenResolution.width
        );
  }

  return type;
};

const AddingFrames = ({
  originalTourId,
  selectedFrameIndex,
  demoIdsTourIsBasedOn,
  deeplinkProtocol,
  tour,
  onDropdownButtonClick,
  useNavigate,
  openRedirectPage,
}: IAddingFrames) => {
  const [createTourFromUrl] = useCreateTourFromUrlMutation();
  const { uuid, onUpdateEditorTabVisibility } = useTourEditorPageProps();
  const capturedScreenSize = useTourSelector(selectCapturedScreenSize(uuid));

  const [
    isCreateTourFromLiveAppModalOpen,
    setIsCreateTourFromLiveAppModalOpen,
  ] = useState(false);

  const [isCreateTourFromDemoModalOpen, setIsCreateTourFromDemoModalOpen] =
    useState(false);

  const { organizationId, workspaceId } = useParams<{
    workspaceId: string;
    organizationId: string;
  }>();

  const { data } = useGetDemosQuery({
    organizationId,
    workspaces: workspaceId,
  });

  const screenResolution: ScreenResolution | null = useMemo(() => {
    return {
      width:
        tour?.draftSettings?.screenResolution?.width ??
        capturedScreenSize?.width,
      height:
        tour?.draftSettings?.screenResolution?.height ??
        capturedScreenSize?.height,
      type: getScreenResolutionTypeFromSettings(tour, capturedScreenSize),
    };
  }, [tour, capturedScreenSize]);

  const demos = data?.items;

  const {
    navigateToWebTourCapture,
    navigateToTourCapture,
    navigateToDemoPage,
    navigateToRecorder,
    navigateToCreateOverlay,
  } = useNavigate();

  const handleFromALiveAppClick = () => {
    setIsCreateTourFromLiveAppModalOpen(true);
  };

  const handleFromADemoClick = () => {
    setIsCreateTourFromDemoModalOpen(true);
  };

  const handleContinue = async (url) => {
    if (!organizationId) {
      return;
    }
    setIsCreateTourFromLiveAppModalOpen(false);

    let fileServiceToken;
    let playerToken;
    let tourId;

    if (isDesktop) {
      onUpdateEditorTabVisibility?.(false);
      const response = (await createTourFromUrl({
        organizationId,
        url,
        description: 'Tour description',
        tourName: 'Tour name',
        source: 'desktop',
      })) as {
        data: Tour & { playerToken: string };
      };

      fileServiceToken = response.data.fileServiceToken;
      playerToken = response.data.playerToken;
      tourId = response.data.id;
    }

    navigateToWebTourCapture({
      appUrl: url,
      navigateCb: openRedirectPage,
      tourId: tourId,
      originalTourId,
      insertIndex: selectedFrameIndex + 1,
      screenResolution,
      fileServiceToken,
      playerToken,
      tourEditorTabId: Number(uuid),
      isOpenedFromEditor: true,
    });
  };

  const navigateToTourFromDemo = (
    id: any,
    originalTourId?: any,
    insertIndex?: number
  ) => {
    setIsCreateTourFromDemoModalOpen(false);
    if (isDesktop || tour.source === 'platform') {
      onUpdateEditorTabVisibility?.(false);
      return navigateToTourCapture({
        demoId: id,
        originalTourId,
        insertIndex,
        screenResolution,
        tourEditorTabId: Number(uuid),
        isOpenedFromEditor: true,
      });
    }
    let url = `${deeplinkProtocol}://tour-create/${organizationId}/workspace/${workspaceId}/demo/${id}/tour-capture`;
    if (originalTourId) {
      url += `?originalTourId=${originalTourId}&insertIndex=${insertIndex}`;
    }

    if (screenResolution) {
      url += `&width=${screenResolution.width}&height=${screenResolution.height}&type=${screenResolution.type}`;
    }
    openRedirectPage(url);
  };

  return (
    <>
      <Dropdown
        selectedFrameIndex={selectedFrameIndex + 1}
        isCaptureFromDemoDisabled={!demos?.length}
        onDropdownButtonClick={onDropdownButtonClick}
        onFromADemoClick={handleFromADemoClick}
        onFromALiveAppClick={handleFromALiveAppClick}
        tour={tour}
      />
      <CreateTourModal
        open={isCreateTourFromDemoModalOpen}
        onClose={() => setIsCreateTourFromDemoModalOpen(false)}
        title={title}
        description="Choose a demo you want to add the frames from"
        originalTourId={originalTourId}
        selectedFrameIndex={selectedFrameIndex}
        demoIdsTourIsBasedOn={demoIdsTourIsBasedOn}
        goToDemo={navigateToDemoPage}
        createTourFromDemo={navigateToTourFromDemo}
        handleOpenRecorder={navigateToRecorder}
        goToCreateOverlayDemoPage={() => navigateToCreateOverlay(workspaceId)}
      />
      <CreateTourFromScratch
        open={isCreateTourFromLiveAppModalOpen}
        title={title}
        onSubmit={handleContinue}
        onClose={() => setIsCreateTourFromLiveAppModalOpen(false)}
      />
    </>
  );
};

export default AddingFrames;
